/* BBM PLP product card — pack selection and add to cart.
 *
 * Progressive enhancement: the action button ships disabled and is only
 * enabled here, so a visitor without JS follows the card title to the PDP
 * rather than meeting a dead control.
 *
 * Cart payload follows the Biscuit Bundles private integration guide (v1, Aug
 * 2026, ticket #443910): the PARENT variant plus exactly two line item
 * properties, expanded into components by the app's cart transform.
 *
 *   _bundleId       BXXX-PARENTVARIANTID, regenerated for EVERY request so
 *                   Shopify does not merge separate bundle additions into one
 *                   cart line.
 *   _selectedItems  JSON *string*: [{ i: "<variant id>", q: <int>, s: <step> }]
 *
 * Two kinds of selection go into _selectedItems:
 *   - BASE ITEMS: components from steps with no customer choice (a gift box,
 *     an included assortment). Rendered into data-base-items as
 *     "id:qty:step,id:qty:step". Present on every card, chips or not.
 *   - The chosen PACK, when the card has a pack selector.
 *
 * Optional add-on steps are deliberately absent: adding from the listing page
 * skips them, which is what BBM agreed with the vendor on 2026-08-26.
 *
 *   _biscuitsCustomProperty1  Not in the integration guide, but the app's own
 *                   PDP form always sends it. Without it the rate engine
 *                   returns "Shipping not available" at checkout; with just
 *                   the position marker below, rates come back (BBYM Dev 2,
 *                   All The Flavors 100-Pack, 2026-09-14 — isolated one
 *                   property at a time, with plain-line controls either side).
 */
(function () {
  'use strict';

  var TOKEN_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  var BISCUITS_CUSTOM_PROPERTY = JSON.stringify({ _biscuitsCustomPropPosition: 'a' });

  function generateBundleToken() {
    var token = 'B';
    for (var i = 0; i < 3; i += 1) {
      token += TOKEN_CHARS.charAt(Math.floor(Math.random() * TOKEN_CHARS.length));
    }
    return token;
  }

  function formatMoney(cents) {
    // Matches Liquid's money_without_trailing_zeros, so the initial server
    // render and any JS update agree — the design shows "$70", not "$70.00".
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + amount;
  }

  function parseBaseItems(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map(function (chunk) {
        var bits = chunk.split(':');
        if (bits.length < 3 || !bits[0]) return null;
        return { i: String(bits[0]), q: parseInt(bits[1], 10) || 1, s: parseInt(bits[2], 10) || 1 };
      })
      .filter(Boolean);
  }

  var BbymPackCard = function () {
    return Reflect.construct(HTMLElement, [], BbymPackCard);
  };

  BbymPackCard.prototype = Object.create(HTMLElement.prototype);
  BbymPackCard.prototype.constructor = BbymPackCard;
  Object.setPrototypeOf(BbymPackCard, HTMLElement);

  BbymPackCard.prototype.connectedCallback = function () {
    this.button = this.querySelector('[data-bbym-atc]');
    if (!this.button) return;

    this.priceEl = this.querySelector('[data-bbym-price]');
    this.inputs = Array.prototype.slice.call(
      this.querySelectorAll('.bbym-plp-card__pack-input')
    );
    this.card = this.closest('[data-bbym-plp-card]');
    this.image = this.card ? this.card.querySelector('.bbym-plp-card__image') : null;

    // The theme renders a cart-drawer (layout/theme.liquid:428). Mirror what
    // product-form.js does so an add from the listing page opens the slide-out
    // cart exactly as an add from the PDP does.
    this.cartUi =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.cartEnabled = this.dataset.cartEnabled === 'true';
    this.productUrl = this.dataset.productUrl || '';
    this.baseItems = parseBaseItems(this.dataset.baseItems);
    // A plain Shopify product: added by variant id with no bundle properties.
    this.simpleVariant = parseInt(this.dataset.simpleVariant, 10) || 0;
    this.packStep = parseInt(this.dataset.step, 10) || 0;
    this.packQty = parseInt(this.dataset.quantityPerItem, 10) || 1;

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);

    // A card with no chips is a fixed product — still fully addable.
    this.inputs.forEach(
      function (input) {
        input.addEventListener('change', this.onChange);
      }.bind(this)
    );
    this.button.addEventListener('click', this.onClick);

    this.button.disabled = false;
    if (this.inputs.length) this.sync();
  };

  BbymPackCard.prototype.selected = function () {
    for (var i = 0; i < this.inputs.length; i += 1) {
      if (this.inputs[i].checked) return this.inputs[i];
    }
    return null;
  };

  /* Selecting a pack updates price and imagery in place — no network call. */
  BbymPackCard.prototype.sync = function () {
    var input = this.selected();
    if (!input) return;

    var price = parseInt(input.dataset.price, 10);
    if (this.priceEl && !isNaN(price) && price > 0) {
      this.priceEl.textContent = formatMoney(price);
    }

    var image = input.dataset.image;
    if (this.image && image) {
      this.image.setAttribute('src', image);
      this.image.removeAttribute('srcset');
    }
  };

  BbymPackCard.prototype.onChange = function () {
    this.sync();
  };

  BbymPackCard.prototype.selectedItems = function () {
    var items = this.baseItems.slice();
    var input = this.selected();
    if (input && this.packStep) {
      items.push({ i: String(input.value), q: this.packQty, s: this.packStep });
    }
    return items;
  };

  BbymPackCard.prototype.onClick = function (event) {
    event.preventDefault();

    if (!this.cartEnabled) {
      if (this.productUrl) window.location.href = this.productUrl;
      return;
    }

    var parentVariantId = parseInt(this.dataset.parentVariantId, 10);
    if (!parentVariantId) return;

    var input = this.selected();
    if (this.inputs.length && (!input || input.disabled)) return;

    var isSimple = this.simpleVariant > 0;
    var items = isSimple ? [] : this.selectedItems();
    if (!isSimple && !items.length) {
      // Nothing resolvable to send — the PDP is the honest fallback.
      if (this.productUrl) window.location.href = this.productUrl;
      return;
    }

    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;

    var root =
      window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    var line = isSimple
      ? { id: this.simpleVariant, quantity: 1 }
      : {
          id: parentVariantId,
          quantity: 1,
          properties: {
            _bundleId: generateBundleToken() + '-' + parentVariantId,
            _selectedItems: JSON.stringify(items),
            _biscuitsCustomProperty1: BISCUITS_CUSTOM_PROPERTY
          }
        };

    var body = { items: [line] };

    // Ask for the cart drawer's sections back in the same response so it can
    // re-render without a second round trip — same contract product-form.js uses.
    if (this.cartUi && typeof this.cartUi.getSectionsToRender === 'function') {
      body.sections = this.cartUi.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
      if (typeof this.cartUi.setActiveElement === 'function') {
        this.cartUi.setActiveElement(document.activeElement);
      }
    }

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json();
      })
      .then(
        function (data) {
          if (data && data.status) throw new Error(data.description || 'cart error');

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bbym-plp-card',
              productVariantId: parentVariantId,
              cartData: data
            });
          }

          // Opens the slide-out cart. renderContents() does not clear the
          // drawer's own `is-empty` class, and component-cart-drawer.css hides
          // the line items while it is set — so an add into an EMPTY cart
          // showed the total and upsells with no product (Asana, 2026-09-14).
          // product-form.js:97 clears it the same way before rendering.
          if (this.cartUi && this.cartUi.classList.contains('is-empty')) {
            this.cartUi.classList.remove('is-empty');
          }
          if (this.cartUi && typeof this.cartUi.renderContents === 'function') {
            this.cartUi.renderContents(data);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        }.bind(this)
      )
      .catch(function () {
        /* Error surfacing is a follow-up; failing quietly beats a false success. */
      })
      .then(
        function () {
          this.button.removeAttribute('aria-busy');
          this.button.disabled = false;
        }.bind(this)
      );
  };

  if (!customElements.get('bbym-pack-card')) {
    customElements.define('bbym-pack-card', BbymPackCard);
  }
})();
